import React, { useEffect, useState, memo, useCallback, useMemo } from 'react';
import { useParams, useHistory, useRouteMatch, Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlay,
  faDownload,
  faEdit,
  faTrash,
  faMusic,
  faClock,
  faUser,
  faCalendar,
  faTags,
  faTools,
  faFileAudio,
  faCompactDisc,
  faMicrophone,
  faUpload,
  faWaveSquare,
  faMagic,
  faRobot,
  faGuitar,
  faPenNib,
  faQuestionCircle,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { useMediaQuery } from "react-responsive";
import { getProjectById, deleteProject, generateBeatsChords, checkBeatsChordStatus, generateLyrics, checkLyricsStatus, generateMusicId, checkMusicIdStatus, generateAutoMaster, checkAutoMasterStatus, generateStems, checkStemsStatus } from '../../../service/beats-api';
import { useAudioPlayer } from "../context/AudioContext";
import ToolsMenu from "./ToolsMenu";
import BeatsChordVisualization from './BeatsChordVisualization';
import LyricsVisualization from './LyricsVisualization';
import MusicIdData from './MusicIdData';
import AutoMasterData from './AutoMasterData';
import StemsVisualization from './StemsVisualization';
import '../components/components.css';

const ProjectPage = memo(() => {
  const { projectId } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generatingLyrics, setGeneratingLyrics] = useState(false);
  const [lyricsGenerationStatus, setLyricsGenerationStatus] = useState(null);
  const [generatingMusicId, setGeneratingMusicId] = useState(false);
  const [musicIdGenerationStatus, setMusicIdGenerationStatus] = useState(null);
  const [generatingAutoMaster, setGeneratingAutoMaster] = useState(false);
  const [autoMasterGenerationStatus, setAutoMasterGenerationStatus] = useState(null);
  const [generatingStems, setGeneratingStems] = useState(false);
  const [stemsGenerationStatus, setStemsGenerationStatus] = useState(null);
  const { playAudio, closePlayer } = useAudioPlayer();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1024px)" });

  // Tool definitions with their corresponding asset types and details
  const toolsConfig = {
    'music-identification': {
      label: 'Music ID',
      icon: faMusic,
      description: 'Identify songs, melodies, and audio samples using AI',
      assetTypes: ['MUSIC_ID'], // Assets that would be created by this tool
      features: [
        'Real-time audio recognition',
        'Melody and harmony analysis',
        'Genre classification',
        'BPM detection'
      ]
    },
    'ai-stems-separator': {
      label: 'AI Stems',
      icon: faWaveSquare,
      description: 'Separate audio tracks into individual stems (vocals, drums, bass, etc)',
      assetTypes: ['STEM'],
      features: [
        'Vocal isolation',
        'Drum separation',
        'Bass extraction',
        'Instrumental stems'
      ]
    },
    'auto-mastering': {
      label: 'Auto Master',
      icon: faMagic,
      description: 'Enhance your track with AI-powered mastering tools',
      assetTypes: ['MASTERED'],
      features: [
        'Loudness optimization',
        'Low/high cut frequencies',
        'Dynamic range control',
        'Stereo imaging enhancement'
      ]
    },
    'ai-voice-changer': {
      label: 'Voice AI',
      icon: faMicrophone,
      description: 'Voice AI swap only (two available variants - male, female)',
      assetTypes: ['VOICE_SWAPPED'],
      features: [
        'Male voice variant',
        'Female voice variant',
        'High-quality conversion',
        'Preserve timing'
      ]
    },
    'beats-chords': {
      label: 'Beats & Chords',
      icon: faGuitar,
      description: 'Generate beats and chord progressions with AI',
      assetTypes: ['BEATS_CHORDS'],
      features: [
        'Beat generation',
        'Chord progressions',
        'Rhythm patterns',
        'Harmonic analysis'
      ]
    },
    'lyrics-ai': {
      label: 'Lyrics AI',
      icon: faPenNib,
      description: 'Generate creative lyrics with AI assistance',
      assetTypes: ['LYRICS_AI'],
      features: [
        'Lyric generation',
        'Rhyme schemes',
        'Theme development',
        'Style adaptation'
      ]
    }
  };

  // Get current active tool from URL
  const getCurrentTool = () => {
    const pathSegments = location.pathname.split('/');
    const toolSegment = pathSegments[pathSegments.length - 1];
    return toolsConfig[toolSegment] || null;
  };

  const currentTool = getCurrentTool();

  // Check if project has assets for the current tool
  const hasRelevantAssets = () => {
    if (!currentTool || !project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      currentTool.assetTypes.includes(asset.type)
    );
  };

  // Check if project has original audio assets for generation
  const hasOriginalAssets = () => {
    if (!project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      asset.type === 'ORIGINAL' && asset.status === 'READY'
    );
  };

  // Check if project has ready beats/chords assets
  const hasReadyBeatsChords = () => {
    if (!project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      asset.type === 'BEATS_CHORDS' && asset.status === 'READY'
    );
  };

  // Check if project has ready lyrics assets
  const hasReadyLyrics = () => {
    if (!project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      asset.type === 'LYRICS_AI' && asset.status === 'READY'
    );
  };

  // Check if project has ready music identification assets
  const hasReadyMusicId = () => {
    if (!project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      asset.type === 'MUSIC_ID' && asset.status === 'READY'
    );
  };

  // Check if project has ready automaster assets
  const hasReadyAutoMaster = () => {
    if (!project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      asset.type === 'MASTERED' && asset.status === 'READY'
    );
  };

  // Check if project has ready stems assets
  const hasReadyStems = () => {
    if (!project?.audio_assets) return false;
    return project.audio_assets.some(asset => 
      asset.type === 'STEM' && asset.status === 'READY'
    );
  };

  // Hide/close previous audio on page load
  useEffect(() => {
    // This effect runs on component mount to close any previous audio
    if (closePlayer) {
      closePlayer();
    }
  }, []); // Run only once on mount, no dependencies needed

  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProjectById(projectId);
      if (response.success) {
        console.log('Project loaded successfully:', response.project);
        
        // Log audio assets and their URLs for debugging
        if (response.project.audio_assets && response.project.audio_assets.length > 0) {
          console.log('Audio assets with presigned URLs:');
          response.project.audio_assets.forEach((asset, index) => {
            console.log(`Asset ${index + 1}:`, {
              id: asset.id,
              file_name: asset.file_name,
              type: asset.type,
              status: asset.status,
              download_url: asset.download_url,
              preview_url: asset.preview_url,
              has_download_url: !!asset.download_url,
              has_preview_url: !!asset.preview_url
            });
          });
        }
        
        setProject(response.project);
      } else {
        setError('Project not found or you do not have access to it.');
      }
    } catch (err) {
      console.error('Error loading project:', err);
      setError('Failed to load project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await deleteProject(projectId);
      if (response.success) {
        // Redirect to home after successful deletion
        history.push(url.replace(`/project/${projectId}`, ''));
      } else {
        alert('Failed to delete project. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Memoize the handlePlayAudio function to prevent re-renders
  const handlePlayAudio = useCallback((asset) => {
    if (asset.download_url) {
      // Ensure we pass the projectId correctly as a string
      const currentProjectId = String(projectId);
      console.log('[ProjectPage] Playing audio with projectId:', currentProjectId);
      playAudio(asset.download_url, asset.file_name, currentProjectId);
    }
  }, [playAudio, projectId]);

  const handleDownloadBeatsChords = (asset) => {
    const data = {
      file_name: asset.file_name,
      beats: asset.beats,
      chords: asset.chords,
      created_at: asset.created_at
    };
    
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${asset.file_name.replace(/\.[^/.]+$/, '')}_beats_chords.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGenerateBeatsChords = async () => {
    if (!hasOriginalAssets()) {
      alert('Please upload an original audio file first to generate beats and chords.');
      return;
    }

    setGenerating(true);
    setGenerationStatus('Starting generation...');
    
    try {
      const response = await generateBeatsChords(projectId);
      if (response.success) {
        setGenerationStatus('Generation started successfully!');
        
        // Start polling for status
        const assetId = response.asset.id;
        pollGenerationStatus(assetId);
        
        // Reload project to show the new processing asset
        setTimeout(() => {
          loadProject();
        }, 1000);
      } else {
        setGenerationStatus('Failed to start generation: ' + response.error);
        setGenerating(false);
      }
    } catch (err) {
      console.error('Error generating beats and chords:', err);
      setGenerationStatus('Failed to start generation. Please try again.');
      setGenerating(false);
    }
  };

  const handleGenerateLyrics = async () => {
    if (!hasOriginalAssets()) {
      alert('Please upload an original audio file first to generate lyrics.');
      return;
    }

    setGeneratingLyrics(true);
    setLyricsGenerationStatus('Starting lyrics generation...');
    
    try {
      const response = await generateLyrics(projectId);
      if (response.success) {
        setLyricsGenerationStatus('Lyrics generation started successfully!');
        
        // Start polling for status
        const assetId = response.asset.id;
        pollLyricsGenerationStatus(assetId);
        
        // Reload project to show the new processing asset
        setTimeout(() => {
          loadProject();
        }, 1000);
      } else {
        setLyricsGenerationStatus('Failed to start lyrics generation: ' + response.error);
        setGeneratingLyrics(false);
      }
    } catch (err) {
      console.error('Error generating lyrics:', err);
      setLyricsGenerationStatus('Failed to start lyrics generation. Please try again.');
      setGeneratingLyrics(false);
    }
  };

  const handleGenerateMusicId = async () => {
    if (!hasOriginalAssets()) {
      alert('Please upload an original audio file first to identify music.');
      return;
    }

    setGeneratingMusicId(true);
    setMusicIdGenerationStatus('Starting music identification...');
    
    try {
      const response = await generateMusicId(projectId);
      if (response.success) {
        setMusicIdGenerationStatus('Music identification started successfully!');
        
        // Start polling for status
        const assetId = response.asset.id;
        pollMusicIdGenerationStatus(assetId);
        
        // Reload project to show the new processing asset
        setTimeout(() => {
          loadProject();
        }, 1000);
      } else {
        setMusicIdGenerationStatus('Failed to start music identification: ' + response.error);
        setGeneratingMusicId(false);
      }
    } catch (err) {
      console.error('Error generating music identification:', err);
      setMusicIdGenerationStatus('Failed to start music identification. Please try again.');
      setGeneratingMusicId(false);
    }
  };

  const handleGenerateAutoMaster = async () => {
    if (!hasOriginalAssets()) {
      alert('Please upload an original audio file first to generate auto master.');
      return;
    }

    setGeneratingAutoMaster(true);
    setAutoMasterGenerationStatus('Starting auto mastering...');
    
    try {
      const response = await generateAutoMaster(projectId);
      if (response.success) {
        setAutoMasterGenerationStatus('Auto mastering started successfully!');
        
        // Start polling for status
        const assetId = response.asset.id;
        pollAutoMasterGenerationStatus(assetId);
        
        // Reload project to show the new processing asset
        setTimeout(() => {
          loadProject();
        }, 1000);
      } else {
        setAutoMasterGenerationStatus('Failed to start auto mastering: ' + response.error);
        setGeneratingAutoMaster(false);
      }
    } catch (err) {
      console.error('Error generating auto master:', err);
      setAutoMasterGenerationStatus('Failed to start auto mastering. Please try again.');
      setGeneratingAutoMaster(false);
    }
  };

  const handleGenerateStems = async () => {
    if (!hasOriginalAssets()) {
      alert('Please upload an original audio file first to generate stems.');
      return;
    }

    setGeneratingStems(true);
    setStemsGenerationStatus('Starting stems generation...');
    
    try {
      const response = await generateStems(projectId);
      if (response.success) {
        setStemsGenerationStatus('Stems generation started successfully!');
        
        // Start polling for status
        const assetId = response.asset.id;
        pollStemsGenerationStatus(assetId);
        
        // Reload project to show the new processing asset
        setTimeout(() => {
          loadProject();
        }, 1000);
      } else {
        setStemsGenerationStatus('Failed to start stems generation: ' + response.error);
        setGeneratingStems(false);
      }
    } catch (err) {
      console.error('Error generating stems:', err);
      setStemsGenerationStatus('Failed to start stems generation. Please try again.');
      setGeneratingStems(false);
    }
  };

  const pollGenerationStatus = async (assetId) => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await checkBeatsChordStatus(projectId, assetId);
        if (response.success) {
          const jobStatus = response.job_status;
          
          if (jobStatus.status === 'SUCCEEDED') {
            setGenerationStatus('Generation completed successfully!');
            setGenerating(false);
            loadProject(); // Reload to show updated asset
            return;
          } else if (jobStatus.status === 'FAILED') {
            setGenerationStatus('Generation failed. Please try again.');
            setGenerating(false);
            return;
          } else {
            // Still processing
            setGenerationStatus(`Processing... (${jobStatus.status})`);
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setGenerationStatus('Generation is taking longer than expected. Please check back later.');
          setGenerating(false);
        }
      } catch (err) {
        console.error('Error checking generation status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setGenerationStatus('Unable to check generation status. Please refresh the page.');
          setGenerating(false);
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(poll, 5000);
  };

  const pollLyricsGenerationStatus = async (assetId) => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await checkLyricsStatus(projectId, assetId);
        if (response.success) {
          const jobStatus = response.job_status;
          
          if (jobStatus.status === 'SUCCEEDED') {
            setLyricsGenerationStatus('Lyrics generation completed successfully!');
            setGeneratingLyrics(false);
            loadProject(); // Reload to show updated asset
            return;
          } else if (jobStatus.status === 'FAILED') {
            setLyricsGenerationStatus('Lyrics generation failed. Please try again.');
            setGeneratingLyrics(false);
            return;
          } else {
            // Still processing
            setLyricsGenerationStatus(`Processing lyrics... (${jobStatus.status})`);
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setLyricsGenerationStatus('Lyrics generation is taking longer than expected. Please check back later.');
          setGeneratingLyrics(false);
        }
      } catch (err) {
        console.error('Error checking lyrics generation status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setLyricsGenerationStatus('Unable to check lyrics generation status. Please refresh the page.');
          setGeneratingLyrics(false);
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(poll, 5000);
  };

  const pollMusicIdGenerationStatus = async (assetId) => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await checkMusicIdStatus(projectId, assetId);
        if (response.success) {
          const jobStatus = response.job_status;
          
          if (jobStatus.status === 'SUCCEEDED') {
            setMusicIdGenerationStatus('Music identification completed successfully!');
            setGeneratingMusicId(false);
            loadProject(); // Reload to show updated asset
            return;
          } else if (jobStatus.status === 'FAILED') {
            setMusicIdGenerationStatus('Music identification failed. Please try again.');
            setGeneratingMusicId(false);
            return;
          } else {
            // Still processing
            setMusicIdGenerationStatus(`Identifying music... (${jobStatus.status})`);
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setMusicIdGenerationStatus('Music identification is taking longer than expected. Please check back later.');
          setGeneratingMusicId(false);
        }
      } catch (err) {
        console.error('Error checking music identification status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setMusicIdGenerationStatus('Unable to check music identification status. Please refresh the page.');
          setGeneratingMusicId(false);
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(poll, 5000);
  };

  const pollAutoMasterGenerationStatus = async (assetId) => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await checkAutoMasterStatus(projectId, assetId);
        if (response.success) {
          const jobStatus = response.job_status;
          
          if (jobStatus.status === 'SUCCEEDED') {
            setAutoMasterGenerationStatus('Auto mastering completed successfully!');
            setGeneratingAutoMaster(false);
            loadProject(); // Reload to show updated asset
            return;
          } else if (jobStatus.status === 'FAILED') {
            setAutoMasterGenerationStatus('Auto mastering failed. Please try again.');
            setGeneratingAutoMaster(false);
            return;
          } else {
            // Still processing
            setAutoMasterGenerationStatus(`Processing... (${jobStatus.status})`);
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setAutoMasterGenerationStatus('Auto mastering is taking longer than expected. Please check back later.');
          setGeneratingAutoMaster(false);
        }
      } catch (err) {
        console.error('Error checking auto master status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setAutoMasterGenerationStatus('Unable to check auto master status. Please refresh the page.');
          setGeneratingAutoMaster(false);
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(poll, 5000);
  };

  const pollStemsGenerationStatus = async (assetId) => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await checkStemsStatus(projectId, assetId);
        if (response.success) {
          const jobStatus = response.job_status;
          
          if (jobStatus.status === 'SUCCEEDED') {
            setStemsGenerationStatus('Stems generation completed successfully!');
            setGeneratingStems(false);
            loadProject(); // Reload to show updated asset
            return;
          } else if (jobStatus.status === 'FAILED') {
            setStemsGenerationStatus('Stems generation failed. Please try again.');
            setGeneratingStems(false);
            return;
          } else {
            // Still processing
            setStemsGenerationStatus(`Processing stems... (${jobStatus.status})`);
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setStemsGenerationStatus('Stems generation is taking longer than expected. Please check back later.');
          setGeneratingStems(false);
        }
      } catch (err) {
        console.error('Error checking stems generation status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setStemsGenerationStatus('Unable to check stems generation status. Please refresh the page.');
          setGeneratingStems(false);
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(poll, 5000);
  };

  const handleRunTool = () => {
    if (currentTool?.label === 'Beats & Chords') {
      handleGenerateBeatsChords();
    } else if (currentTool?.label === 'Lyrics AI') {
      handleGenerateLyrics();
    } else if (currentTool?.label === 'Music ID') {
      handleGenerateMusicId();
    } else if (currentTool?.label === 'Auto Master') {
      handleGenerateAutoMaster();
    } else if (currentTool?.label === 'AI Stems') {
      handleGenerateStems();
    } else {
      // Button click handler for other tools - can be implemented later
      console.log(`${currentTool.label} tool clicked`);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const mb = (bytes / (1024 * 1024)).toFixed(1);
    return `${mb} MB`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'READY': { bg: 'success', text: 'Ready' },
      'PROCESSING': { bg: 'warning', text: 'Processing' },
      'UPLOADING': { bg: 'info', text: 'Uploading' },
      'ERROR': { bg: 'danger', text: 'Error' },
      'DELETED': { bg: 'secondary', text: 'Deleted' }
    };
    
    const config = statusConfig[status] || { bg: 'secondary', text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const getAssetTypeIcon = (type) => {
    const iconMap = {
      'ORIGINAL': faMusic,
      'STEM': faWaveSquare,
      'MASTERED': faMagic,
      'VOICE_SWAPPED': faRobot,
      'PREVIEW': faPlay,
      'TEMP': faClock,
      'BEATS_CHORDS': faGuitar,
      'LYRICS_AI': faPenNib,
      'MUSIC_ID': faMusic
    };
    return iconMap[type] || faFileAudio;
  };

  const renderBeatsChordData = (asset) => {
    if (!asset.beats && !asset.chords) return null;
    
    return (
      <div className="mt-3">
        {asset.beats && Object.keys(asset.beats).length > 0 && (
          <div className="mb-2">
            <h6 className="text-muted mb-1">Beats Data:</h6>
            <pre className="small text-muted" style={{ maxHeight: '100px', overflow: 'auto', fontSize: 'var(--font-sm)' }}>
              {JSON.stringify(asset.beats, null, 2)}
            </pre>
          </div>
        )}
        {asset.chords && Object.keys(asset.chords).length > 0 && (
          <div className="mb-2">
            <h6 className="text-muted mb-1">Chords Data:</h6>
            <pre className="small text-muted" style={{ maxHeight: '100px', overflow: 'auto', fontSize: 'var(--font-sm)' }}>
              {JSON.stringify(asset.chords, null, 2)}
            </pre>
          </div>
        )}
        <div className="mt-2">
          <Button 
            size="sm"
            variant="outline-light"
            className="beats-btn-outline"
            onClick={() => handleDownloadBeatsChords(asset)}
            title="Download beats and chords data as JSON"
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" style={{ fontSize: 'var(--font-sm)' }} />
            Download JSON
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container fluid className="beats-component-container py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="light" />
          <p className="mt-3 text-muted">Please Wait <br/>Processing Audio Assets...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="beats-component-container py-4">
        <Alert 
          variant="dark"
          style={{
            backgroundColor: 'rgba(179, 95, 208, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(179, 95, 208, 0.3)',
            borderRadius: '12px',
            color: '#d9d9d9'
          }}
        >
          {error}
        </Alert>
        <Button 
          as={Link}
          to={url.replace(`/project/${projectId}`, '')}
          variant="outline-light"
          className="beats-btn-outline"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Home
        </Button>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container fluid className="beats-component-container py-4">
        <Alert 
          variant="dark"
          style={{
            backgroundColor: 'rgba(179, 95, 208, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(179, 95, 208, 0.3)',
            borderRadius: '12px',
            color: '#d9d9d9'
          }}
        >
          Project not found.
        </Alert>
        <Button 
          as={Link}
          to={url.replace(`/project/${projectId}`, '')}
          variant="outline-light"
          className="beats-btn-outline"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="beats-component-container py-2" style={{ minHeight: 'auto', height: 'auto' }}>
      <Row style={{ minHeight: 'auto', height: 'auto' }}>
        {/* Left Tools Menu - Desktop Only */}
        {!isTabletOrMobile && (
          <Col md={3} lg={2} className="p-0">
            <ToolsMenu />
          </Col>
        )}

        {/* Main Content */}
        <Col md={isTabletOrMobile ? 12 : 9} lg={isTabletOrMobile ? 12 : 10} className={isTabletOrMobile ? 'p-2' : 'ps-3 p-2'} style={{ minHeight: 'auto', height: 'auto' }}>
          {/* Mobile Tools Menu */}
          {isTabletOrMobile && (
            <Row className="mb-2">
              <Col>
                <ToolsMenu />
              </Col>
            </Row>
          )}

          {/* Minimal Header - Only Back Button */}
          <Row className="mb-3">
            <Col>
              <Button 
                as={Link}
                to={url.replace(`/project/${projectId}`, '')}
                variant="link"
                className="text-decoration-none p-0"
                style={{ 
                  color: 'var(--purple-accent)',
                  border: 'none',
                  background: 'none'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Home
              </Button>
            </Col>
          </Row>

          {/* Audio Assets and Tool Details - Combined Section */}
          <Row className="mb-3">
            <Col lg={8}>
              {/* Audio Assets Section */}
              <Card className="beats-card mb-3">
                <Card.Body className="py-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0" style={{ color: '#d9d9d9' }}>Audio Assets</h5>
                    <Badge 
                      bg="secondary"
                      style={{ fontSize: 'var(--font-sm)', fontWeight: '400', backgroundColor: '#2589BD', color: 'white', opacity: 0.9 }}
                    >
                      {project.asset_count} files
                    </Badge>
                  </div>
                  
                  {project.audio_assets && project.audio_assets.length > 0 ? (
                    <div className="audio-assets-list" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                      {project.audio_assets
                        .filter(asset => asset.type === 'ORIGINAL' || asset.type === 'MASTERED')
                        .sort((a, b) => {
                          // Sort MASTERED assets to the top, then by creation date (newest first)
                          if (a.type === 'MASTERED' && b.type !== 'MASTERED') return -1;
                          if (a.type !== 'MASTERED' && b.type === 'MASTERED') return 1;
                          return new Date(b.created_at) - new Date(a.created_at);
                        })
                        .map((asset, index) => (
                        <div 
                          key={asset.id} 
                          className="beats-list-item p-3 d-flex align-items-center mb-3"
                          style={{ 
                            borderRadius: '8px',
                            border: asset.type === 'MASTERED' 
                              ? '1px solid rgba(179, 95, 208, 0.3)' 
                              : '1px solid rgba(255, 255, 255, 0.1)',
                            backgroundColor: asset.type === 'MASTERED' 
                              ? 'rgba(179, 95, 208, 0.05)' 
                              : 'rgba(255, 255, 255, 0.02)'
                          }}
                        >
                          <div className="me-3">
                            <FontAwesomeIcon 
                              icon={getAssetTypeIcon(asset.type)} 
                              style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xl)' }} 
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <h6 className="mb-1 me-2" style={{ color: '#d9d9d9' }}>{asset.file_name}</h6>
                              {asset.type === 'MASTERED' && (
                                <Badge 
                                  style={{ 
                                    backgroundColor: 'var(--purple-accent)', 
                                    fontSize: 'var(--font-xs)',
                                    fontWeight: '500'
                                  }}
                                >
                                  AI Mastered
                                </Badge>
                              )}
                            </div>
                            <div className="d-flex flex-wrap gap-3 text-muted small">
                              <span>
                                <FontAwesomeIcon icon={faFileAudio} className="me-1" />
                                {asset.format?.toUpperCase() || 'Unknown'}
                              </span>
                              <span>
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                {formatDuration(asset.duration)}
                              </span>
                              <span>
                                <FontAwesomeIcon icon={faCompactDisc} className="me-1" />
                                {formatFileSize(asset.file_size)}
                              </span>
                            </div>

                            <div className="mt-2">
                              <Badge 
                                bg={asset.status === 'READY' ? 'success' : asset.status === 'ERROR' ? 'danger' : 'warning'}
                                style={{ fontSize: 'var(--font-sm)', fontWeight: '400', opacity: 0.9 }}
                              >
                                {asset.status}
                              </Badge>
                            </div>
                            
                            {/* Show beats and chords data for BEATS_CHORDS assets */}
                            {asset.type === 'BEATS_CHORDS' && renderBeatsChordData(asset)}
                          </div>
                          <div className="d-flex gap-2 ms-3">
                            {asset.download_url && asset.status === 'READY' && (
                              <>
                                <Button 
                                  size="sm"
                                  className="beats-btn-purple-ctrl d-flex align-items-center justify-content-center"
                                  onClick={() => handlePlayAudio(asset)}
                                  title="Play audio"
                                  style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '6px',
                                    padding: '0'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlay} style={{ fontSize: 'var(--font-sm)' }} />
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline-light"
                                  className="beats-btn-outline d-flex align-items-center justify-content-center"
                                  onClick={() => window.open(asset.download_url, '_blank')}
                                  title="Download"
                                  style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '6px',
                                    padding: '0'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: 'var(--font-sm)' }} />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <FontAwesomeIcon 
                        icon={faMusic} 
                        style={{ 
                          fontSize: 'var(--font-5xl)', 
                          color: 'var(--purple-accent)',
                          opacity: 0.4
                        }} 
                        className="mb-3"
                      />
                      <h5 className="mb-3" style={{ color: '#d9d9d9' }}>No Audio Assets</h5>
                      <p className="text-muted mb-4">Upload some audio files to get started</p>
                      <Button className="beats-btn-purple">
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        Upload Audio
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Tool Details Section - Now directly below Audio Assets */}
              {currentTool && (
                <Card className="beats-card">
                  <Card.Body className="py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0" style={{ color: '#d9d9d9' }}>
                        <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                        {currentTool.label}
                      </h5>
                    </div>
                    
                    <div 
                      className="tool-details-container" 
                      style={{ 
                        maxHeight: isTabletOrMobile ? '40vh' : '45vh', 
                        overflowY: 'auto',
                        minHeight: '250px'
                      }}
                    >
                      {/* Beats & Chords Visualization */}
                      {currentTool.label === 'Beats & Chords' && (
                        <div className="mb-4">
                          <BeatsChordVisualization project={project} />
                        </div>
                      )}
                      
                      {/* Lyrics Visualization */}
                      {currentTool.label === 'Lyrics AI' && (
                        <div className="mb-4">
                          <LyricsVisualization project={project} />
                        </div>
                      )}
                      
                      {/* Music Identification Data */}
                      {currentTool.label === 'Music ID' && (
                        <div className="mt-4">
                          <h5 className="mb-3">Music Identification Results</h5>
                          {project.audio_assets
                            .filter(asset => asset.type === 'MUSIC_ID' && asset.status === 'READY')
                            .map(asset => (
                              <MusicIdData key={asset.id} asset={asset} />
                            ))}
                          {project.audio_assets.filter(asset => asset.type === 'MUSIC_ID' && asset.status === 'READY').length === 0 && (
                            <div className="text-muted">No music identification results available yet.</div>
                          )}
                        </div>
                      )}
                      
                      {/* Auto Master Data */}
                      {currentTool.label === 'Auto Master' && (
                        <div className="mt-4">
                          <h5 className="mb-3">Auto Mastering Results</h5>
                          {project.audio_assets
                            .filter(asset => asset.type === 'MASTERED' && asset.status === 'READY')
                            .map(asset => (
                              <AutoMasterData key={asset.id} asset={asset} />
                            ))}
                          {project.audio_assets.filter(asset => asset.type === 'MASTERED' && asset.status === 'READY').length === 0 && (
                            <div className="text-muted">No auto mastering results available yet.</div>
                          )}
                        </div>
                      )}
                      
                      {/* Stems Visualization */}
                      {currentTool.label === 'AI Stems' && (
                        <div className="mb-4">
                          <StemsVisualization project={project} />
                        </div>
                      )}
                      
                      {hasRelevantAssets() ? (
                        // Show relevant assets for this tool
                        <div>
                          <p className="text-muted mb-3">
                            Assets created with {currentTool.label}:
                          </p>
                          {project.audio_assets
                            .filter(asset => currentTool.assetTypes.includes(asset.type))
                            .map((asset, index) => (
                              <div 
                                key={asset.id} 
                                className="beats-list-item p-3 d-flex align-items-start mb-3"
                                style={{ 
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                }}
                              >
                                <div className="me-3">
                                  <FontAwesomeIcon 
                                    icon={currentTool.icon} 
                                    style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xl)' }} 
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-1" style={{ color: '#d9d9d9' }}>{asset.file_name}</h6>
                                  <div className="d-flex flex-wrap gap-3 text-muted small">
                                    <span>
                                      <FontAwesomeIcon icon={faFileAudio} className="me-1" />
                                      {asset.format?.toUpperCase() || 'Unknown'}
                                    </span>
                                    <span>
                                      <FontAwesomeIcon icon={faClock} className="me-1" />
                                      {formatDuration(asset.duration)}
                                    </span>
                                    <span>
                                      <Badge 
                                        bg={asset.status === 'READY' ? 'success' : asset.status === 'ERROR' ? 'danger' : 'warning'}
                                        style={{ fontSize: 'var(--font-sm)', fontWeight: '400', opacity: 0.9 }}
                                      >
                                        {asset.status}
                                      </Badge>
                                    </span>
                                  </div>
                                  
                                  {/* Show beats and chords data for BEATS_CHORDS assets */}
                                  {asset.type === 'BEATS_CHORDS' && renderBeatsChordData(asset)}
                                </div>
                                <div className="d-flex gap-2 ms-3">
                                  {asset.download_url && asset.status === 'READY' && (
                                    <>
                                      <Button 
                                        size="sm"
                                        className="beats-btn-purple-ctrl d-flex align-items-center justify-content-center"
                                        onClick={() => handlePlayAudio(asset)}
                                        title="Play audio"
                                        style={{ 
                                          width: '32px', 
                                          height: '32px', 
                                          borderRadius: '6px',
                                          padding: '0'
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faPlay} style={{ fontSize: 'var(--font-sm)' }} />
                                      </Button>
                                      <Button 
                                        size="sm"
                                        variant="outline-light"
                                        className="beats-btn-outline d-flex align-items-center justify-content-center"
                                        onClick={() => window.open(asset.download_url, '_blank')}
                                        title="Download"
                                        style={{ 
                                          width: '32px', 
                                          height: '32px', 
                                          borderRadius: '6px',
                                          padding: '0'
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faDownload} style={{ fontSize: 'var(--font-sm)' }} />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                          {/* Show generation button for stems tool even when assets exist */}
                          {currentTool.label === 'AI Stems' && !hasReadyStems() && (
                            <div className="text-center mt-4">
                              {stemsGenerationStatus && (
                                <Alert 
                                  variant={generatingStems ? "info" : "success"} 
                                  className="mb-3" 
                                  style={{ 
                                    backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(179, 95, 208, 0.3)',
                                    borderRadius: '12px',
                                    color: '#d9d9d9'
                                  }}
                                >
                                  {generatingStems && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                  {stemsGenerationStatus}
                                </Alert>
                              )}
                              
                              <Button 
                                className="beats-btn-purple"
                                onClick={handleRunTool}
                                disabled={generatingStems || !hasOriginalAssets()}
                                title={!hasOriginalAssets() ? "Upload an original audio file first" : ""}
                              >
                                {generatingStems ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Generating Stems...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                                    Generate New Stems
                                  </>
                                )}
                              </Button>
                            </div>
                          )}

                          {/* Show generation button for beats-chords tool even when assets exist */}
                          {currentTool.label === 'Beats & Chords' && !hasReadyBeatsChords() && (
                            <div className="text-center mt-4">
                              {generationStatus && (
                                <Alert 
                                  variant={generating ? "info" : "success"} 
                                  className="mb-3" 
                                  style={{ 
                                    backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(179, 95, 208, 0.3)',
                                    borderRadius: '12px',
                                    color: '#d9d9d9'
                                  }}
                                >
                                  {generating && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                  {generationStatus}
                                </Alert>
                              )}
                              
                              <Button 
                                className="beats-btn-purple"
                                onClick={handleRunTool}
                                disabled={generating || !hasOriginalAssets()}
                                title={!hasOriginalAssets() ? "Upload an original audio file first" : ""}
                              >
                                {generating ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                                    Generate New Beats & Chords
                                  </>
                                )}
                              </Button>
                            </div>
                          )}

                          {/* Show generation button for lyrics tool even when assets exist */}
                          {currentTool.label === 'Lyrics AI' && !hasReadyLyrics() && (
                            <div className="text-center mt-4">
                              {lyricsGenerationStatus && (
                                <Alert 
                                  variant={generatingLyrics ? "info" : "success"} 
                                  className="mb-3" 
                                  style={{ 
                                    backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(179, 95, 208, 0.3)',
                                    borderRadius: '12px',
                                    color: '#d9d9d9'
                                  }}
                                >
                                  {generatingLyrics && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                  {lyricsGenerationStatus}
                                </Alert>
                              )}
                              
                              <Button 
                                className="beats-btn-purple"
                                onClick={handleRunTool}
                                disabled={generatingLyrics || !hasOriginalAssets()}
                                title={!hasOriginalAssets() ? "Upload an original audio file first" : ""}
                              >
                                {generatingLyrics ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Generating Lyrics...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                                    Generate New Lyrics
                                  </>
                                )}
                              </Button>
                            </div>
                          )}

                          {/* Show generation button for music identification tool even when assets exist */}
                          {currentTool.label === 'Music ID' && !hasReadyMusicId() && (
                            <div className="text-center mt-4">
                              {musicIdGenerationStatus && (
                                <Alert 
                                  variant={generatingMusicId ? "info" : "success"} 
                                  className="mb-3" 
                                  style={{ 
                                    backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(179, 95, 208, 0.3)',
                                    borderRadius: '12px',
                                    color: '#d9d9d9'
                                  }}
                                >
                                  {generatingMusicId && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                  {musicIdGenerationStatus}
                                </Alert>
                              )}
                              
                              <Button 
                                className="beats-btn-purple"
                                onClick={handleRunTool}
                                disabled={generatingMusicId || !hasOriginalAssets()}
                                title={!hasOriginalAssets() ? "Upload an original audio file first" : ""}
                              >
                                {generatingMusicId ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Generating Music ID...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                                    Generate New Music ID
                                  </>
                                )}
                              </Button>
                            </div>
                          )}

                          {/* Show generation button for auto master tool even when assets exist */}
                          {currentTool.label === 'Auto Master' && !hasReadyAutoMaster() && (
                            <div className="text-center mt-4">
                              {autoMasterGenerationStatus && (
                                <Alert 
                                  variant={generatingAutoMaster ? "info" : "success"} 
                                  className="mb-3" 
                                  style={{ 
                                    backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(179, 95, 208, 0.3)',
                                    borderRadius: '12px',
                                    color: '#d9d9d9'
                                  }}
                                >
                                  {generatingAutoMaster && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                  {autoMasterGenerationStatus}
                                </Alert>
                              )}
                              
                              <Button 
                                className="beats-btn-purple"
                                onClick={handleRunTool}
                                disabled={generatingAutoMaster || !hasOriginalAssets()}
                                title={!hasOriginalAssets() ? "Upload an original audio file first" : ""}
                              >
                                {generatingAutoMaster ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Generating Auto Master...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                                    Generate New Auto Master
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Show tool details and run button when no relevant assets
                        <div className="text-center py-4">
                          <div className="mb-4">
                            <FontAwesomeIcon 
                              icon={currentTool.icon} 
                              style={{ 
                                fontSize: 'var(--font-5xl)', 
                                color: 'var(--purple-accent)',
                                opacity: 0.8
                              }} 
                              className="mb-3"
                            />
                            <h4 className="mb-3" style={{ color: '#d9d9d9' }}>
                              {currentTool.label}
                            </h4>
                            <p className="text-muted mb-4" style={{ maxWidth: '450px', margin: '0 auto' }}>
                              {currentTool.description}
                            </p>
                          </div>

                          <div className="mb-4">
                            <h6 className="mb-3" style={{ color: '#d9d9d9' }}>Features:</h6>
                            <Row className="justify-content-center">
                              {currentTool.features.map((feature, index) => (
                                <Col key={index} xs={6} md={4} className="mb-2">
                                  <div 
                                    className="feature-item p-2 text-center"
                                    style={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                      borderRadius: '6px',
                                      border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}
                                  >
                                    <small style={{ color: '#d9d9d9' }}>{feature}</small>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>

                          {/* Show generation status for beats-chords tool */}
                          {currentTool.label === 'Beats & Chords' && generationStatus && (
                            <Alert 
                              variant={generating ? "info" : "success"} 
                              className="mb-4" 
                              style={{ 
                                backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(179, 95, 208, 0.3)',
                                borderRadius: '12px',
                                color: '#d9d9d9'
                              }}
                            >
                              {generating && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                              {generationStatus}
                            </Alert>
                          )}

                          {/* Show generation status for lyrics tool */}
                          {currentTool.label === 'Lyrics AI' && lyricsGenerationStatus && (
                            <Alert 
                              variant={generatingLyrics ? "info" : "success"} 
                              className="mb-4" 
                              style={{ 
                                backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(179, 95, 208, 0.3)',
                                borderRadius: '12px',
                                color: '#d9d9d9'
                              }}
                            >
                              {generatingLyrics && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                              {lyricsGenerationStatus}
                            </Alert>
                          )}

                          {/* Show generation status for music identification tool */}
                          {currentTool.label === 'Music ID' && musicIdGenerationStatus && (
                            <Alert 
                              variant={generatingMusicId ? "info" : "success"} 
                              className="mb-4" 
                              style={{ 
                                backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(179, 95, 208, 0.3)',
                                borderRadius: '12px',
                                color: '#d9d9d9'
                              }}
                            >
                              {generatingMusicId && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                              {musicIdGenerationStatus}
                            </Alert>
                          )}

                          {/* Show generation status for auto master tool */}
                          {currentTool.label === 'Auto Master' && autoMasterGenerationStatus && (
                            <Alert 
                              variant={generatingAutoMaster ? "info" : "success"} 
                              className="mb-4" 
                              style={{ 
                                backgroundColor: 'rgba(179, 95, 208, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(179, 95, 208, 0.3)',
                                borderRadius: '12px',
                                color: '#d9d9d9'
                              }}
                            >
                              {generatingAutoMaster && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                              {autoMasterGenerationStatus}
                            </Alert>
                          )}

                          {/* Only show the button if it's not beats-chords tool or if beats-chords don't exist yet */}
                          {((currentTool.label !== 'Beats & Chords' && currentTool.label !== 'Lyrics AI' && currentTool.label !== 'Music ID' && currentTool.label !== 'Auto Master') || 
                           (currentTool.label === 'Beats & Chords' && !hasReadyBeatsChords()) ||
                           (currentTool.label === 'Lyrics AI' && !hasReadyLyrics()) ||
                           (currentTool.label === 'Music ID' && !hasReadyMusicId()) ||
                           (currentTool.label === 'Auto Master' && !hasReadyAutoMaster())) && (
                            <Button 
                              className="beats-btn-purple"
                              size="lg"
                              onClick={handleRunTool}
                              disabled={(generating && currentTool.label === 'Beats & Chords') || 
                                       (generatingLyrics && currentTool.label === 'Lyrics AI') || 
                                       (generatingMusicId && currentTool.label === 'Music ID') ||
                                       (generatingAutoMaster && currentTool.label === 'Auto Master') ||
                                       ((currentTool.label === 'Beats & Chords' || currentTool.label === 'Lyrics AI' || currentTool.label === 'Music ID' || currentTool.label === 'Auto Master') && !hasOriginalAssets())}
                              title={(currentTool.label === 'Beats & Chords' || currentTool.label === 'Lyrics AI' || currentTool.label === 'Music ID' || currentTool.label === 'Auto Master') && !hasOriginalAssets() ? "Upload an original audio file first" : ""}
                            >
                              {((generating && currentTool.label === 'Beats & Chords') || (generatingLyrics && currentTool.label === 'Lyrics AI') || (generatingMusicId && currentTool.label === 'Music ID') || (generatingAutoMaster && currentTool.label === 'Auto Master')) ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={currentTool.icon} className="me-2" />
                                  Use {currentTool.label}
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* Project Metadata - Moved to Right Side of Audio Assets */}
            <Col lg={4}>
              <Card className="beats-card mb-2">
                <Card.Body className="py-3">
                  {/* Project Title and Info - More compact header */}
                  <div className="mb-3">
                    <h4 className="beats-heading mb-1" style={{ color: '#d9d9d9', fontSize: 'var(--font-lg)' }}>{project.name}</h4>
                    {project.description && (
                      <p className="text-muted mb-1" style={{ fontSize: 'var(--font-sm)', lineHeight: '1.3' }}>{project.description}</p>
                    )}
                    <div className="d-flex align-items-center mb-1" style={{ fontSize: 'var(--font-sm)' }}>
                      <FontAwesomeIcon icon={faUser} className="me-1" style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }} />
                      <span style={{ color: '#d9d9d9' }}>{project.owner_username}</span>
                    </div>
                    <div className="d-flex align-items-center" style={{ fontSize: 'var(--font-xs)' }}>
                      <span className="text-muted me-1">ID:</span>
                      <span style={{ color: '#2589BD', fontFamily: 'monospace' }}>{projectId}</span>
                    </div>
                  </div>

                  {/* Project Details - Compact grid layout */}
                  <h6 className="mb-2" style={{ color: '#d9d9d9', fontSize: 'var(--font-base)' }}>Details</h6>
                  <div className="row g-1" style={{ fontSize: 'var(--font-xs)' }}>
                    <div className="col-6">
                      <div className="text-muted">
                        <FontAwesomeIcon icon={faCalendar} className="me-1" style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }} />
                        Created
                      </div>
                      <div style={{ color: '#d9d9d9', fontSize: 'var(--font-xs)' }}>
                        {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Modified</div>
                      <div style={{ color: '#d9d9d9', fontSize: 'var(--font-xs)' }}>
                        {new Date(project.modified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Assets</div>
                      <div style={{ color: '#d9d9d9', fontSize: 'var(--font-xs)' }}>{project.asset_count} files</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Status</div>
                      <div style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }}>Active</div>
                    </div>
                  </div>
                  
                  {project.notes && (
                    <div className="mt-3">
                      <h6 className="text-muted mb-1" style={{ fontSize: 'var(--font-sm)' }}>Notes</h6>
                      <p className="small" style={{ color: '#d9d9d9', fontSize: 'var(--font-xs)', lineHeight: '1.3', margin: 0 }}>{project.notes}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Labels */}
              {project.tags && project.tags.length > 0 && (
                <Card className="beats-card mb-2">
                  <Card.Body className="py-2">
                    <h6 className="mb-2" style={{ color: '#d9d9d9', fontSize: 'var(--font-base)' }}>
                      <FontAwesomeIcon icon={faTags} className="me-1" style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }} />
                      Labels
                    </h6>
                    <div className="d-flex flex-wrap gap-1">
                      {project.tags.map((tag, index) => (
                        <Badge 
                          key={index} 
                          bg="secondary" 
                          className="px-2 py-1"
                          style={{ fontSize: 'var(--font-xs)', fontWeight: '400', backgroundColor: '#2589BD', color: 'white', opacity: 0.8 }}
                        >
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Project Actions Section */}
              <Card className="beats-card mb-2">
                <Card.Body className="py-2">
                  <h6 className="mb-2" style={{ color: '#d9d9d9', fontSize: 'var(--font-base)' }}>
                    <FontAwesomeIcon icon={faTools} className="me-1" style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }} />
                    Actions
                  </h6>
                  <div className="d-grid gap-1">
                    {/* Download Original Music */}
                    <Button 
                      variant="outline-light" 
                      size="sm" 
                      className="beats-btn-outline text-start py-1"
                      disabled
                      style={{ fontSize: 'var(--font-xs)', height: '28px' }}
                    >
                      <FontAwesomeIcon icon={faMusic} className="me-1" style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }} />
                      Download Original
                      <small className="text-muted ms-auto"> Soon</small>
                    </Button>
                    
                    {/* Download All Assets */}
                    <Button 
                      variant="outline-light" 
                      size="sm" 
                      className="beats-btn-outline text-start py-1"
                      disabled
                      style={{ fontSize: 'var(--font-xs)', height: '28px' }}
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-1" style={{ color: 'var(--purple-accent)', fontSize: 'var(--font-xs)' }} />
                      Download All
                      <small className="text-muted ms-auto"> Soon</small>
                    </Button>
                    
                    {/* Delete Project */}
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="text-start py-1"
                      onClick={handleDeleteProject}
                      disabled={deleting}
                      style={{ fontSize: 'var(--font-xs)', height: '28px' }}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-1" style={{ color: '#dc3545', fontSize: 'var(--font-xs)' }} />
                      {deleting ? 'Deleting...' : 'Delete Project'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Bottom Section - Additional Content Space */}
          <Row className="g-3">
            <Col lg={12}>
              {/* This space can be used for additional content in the future */}
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
});

export default ProjectPage; 